import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { selectCurrentDevice, selectCurrentDeviceModel, selectCurrentDeviceStatus, selectNewDeviceState0 } from 'src/app/store/device/device.selector';
import { Observable, Subscription, filter } from 'rxjs';
import { Device } from 'src/app/model/device';
import { DeviceState, DeviceStatus } from 'src/app/model/device-status';
import { DeviceModel } from 'src/app/model/device-model';
import { EDeviceModel } from 'src/app/conf/device-models';
import { DeviceIcon } from 'src/app/conf/device-icons';
import { TranslateService } from '@ngx-translate/core';
import { ICON_CONVERT_AUTOMATICDOOR, ICON_CONVERT_BARRIER, ICON_CONVERT_CAMERA, ICON_CONVERT_GARAGE, ICON_CONVERT_LOAD, ICON_CONVERT_LOCK, ICON_CONVERT_SLIDINGGATE, ICON_CONVERT_SOUND, ICON_CONVERT_SWINGGATE, ICON_CONVERT_TAPPARELLA, ICON_CONVERT_TURNSTILE, ICON_CONVERT_VIRTUAL_DEVICE } from 'src/app/conf/icon';
import { Thermo } from 'src/app/model/thermo';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-device-icon',
	templateUrl: './device-icon.component.html',
	providers: [],
})

export class DeviceIconComponent implements OnInit, OnDestroy {
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceModel$: Observable<DeviceModel | undefined>;
	deviceModelSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	deviceState0$: Observable<DeviceState | undefined>;
	deviceState0Sub: Subscription | undefined;

	device: Device;
	deviceModel: DeviceModel;
	deviceStatus: DeviceStatus;
	iconName: string;

	thermoDevicesSub: Subscription;
	thermo: Thermo;

	isTH = false;
	greenPowerActive = false;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.onResize();
	}

	initSelectors(): void {
		// Store selectors
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.deviceModel$ = this.store.select(selectCurrentDeviceModel);
		this.deviceState0$ = this.store.select(selectNewDeviceState0);
	}

	subscribeAll(): void {
		this.deviceSub = this.device$.pipe(filter(d => !!d))
			.subscribe(device => {
				this.device = device;
			});
		this.deviceState0Sub = this.deviceState0$.pipe(filter(d => !!d))
			.subscribe(deviceState0 => {
				if (deviceState0 !== undefined) {
					this.greenPowerActive = deviceState0.GreenPowerActive;
				}
			});
		this.deviceStatusSub = this.deviceStatus$.pipe(filter(ds => !!ds))
			.subscribe(deviceStatus => {
				this.deviceStatus = deviceStatus;
			});
		this.deviceModelSub = this.deviceModel$.pipe(filter(dm => !!dm))
			.subscribe(deviceModel => {
				this.deviceModel = deviceModel;
				if (deviceModel.Id !== EDeviceModel.MultiIO) {
					this.iconName = 'cm-' + DeviceIcon[deviceModel.Id];
				} else {
					this.iconName = 'cm-' + this.device.IconName.toLowerCase() || 'cm-' + DeviceIcon[deviceModel.Id] || 'cm-device-gateway';
				}
				if (deviceModel.CategoryId === 3) {
					this.isTH = true;
				}
			});
		this.thermoDevicesSub = this.store.select('site', 'deviceThermo').subscribe(
			deviceThermo => {
				if (this.isTH) {
					this.thermo = deviceThermo;
				} else {
					this.thermo = null;
				}
			}
		);
	}

	convertIcon(icon: string): string {
		switch (icon) {
			case ICON_CONVERT_VIRTUAL_DEVICE[0]: {
				return ICON_CONVERT_VIRTUAL_DEVICE[1];
			}
			case ICON_CONVERT_TAPPARELLA[0]: {
				return ICON_CONVERT_TAPPARELLA[1];
			}
			case ICON_CONVERT_LOCK[0]: {
				return ICON_CONVERT_LOCK[1];
			}
			case ICON_CONVERT_SOUND[0]: {
				return ICON_CONVERT_SOUND[1];
			}
			case ICON_CONVERT_CAMERA[0]: {
				return ICON_CONVERT_CAMERA[1];
			}
			case ICON_CONVERT_LOAD[0]: {
				return ICON_CONVERT_LOAD[1];
			}
			case ICON_CONVERT_AUTOMATICDOOR[0]: {
				return ICON_CONVERT_AUTOMATICDOOR[1];
			}
			case ICON_CONVERT_BARRIER[0]: {
				return ICON_CONVERT_BARRIER[1];
			}
			case ICON_CONVERT_SLIDINGGATE[0]: {
				return ICON_CONVERT_SLIDINGGATE[1];
			}
			case ICON_CONVERT_SWINGGATE[0]: {
				return ICON_CONVERT_SWINGGATE[1];
			}
			case ICON_CONVERT_GARAGE[0]: {
				return ICON_CONVERT_GARAGE[1];
			}
			case ICON_CONVERT_TURNSTILE[0]: {
				return ICON_CONVERT_TURNSTILE[1];
			}
			default: {
				return icon;
			}
		}
	}

	ngOnDestroy(): void {
		this.deviceSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.deviceModelSub?.unsubscribe();
		this.thermoDevicesSub?.unsubscribe();
		this.deviceState0Sub?.unsubscribe();
	}

	getStatus(): string {
		if (this.thermo !== undefined && this.thermo !== null) {
			if (this.thermo.error !== null && this.thermo.error !== undefined && this.thermo.error !== "None") {
				return this.translate.instant('GENERIC_ERROR');
			}
			if (this.thermo.on_line !== null && this.thermo.on_line !== undefined && this.thermo.on_line === 1) {
				return this.translate.instant('ONLINE');
			}
			return this.translate.instant('OFFLINE');
		} else {
			if (this.deviceStatus === undefined || !this.deviceStatus.Online) {
				return this.translate.instant('OFFLINE');
			} else if (this.deviceStatus.Errored) {
				return this.translate.instant('GENERIC_ERROR');
			} else {
				return this.translate.instant('ONLINE');
			}
			//return ((this.deviceStatus) ? (this.deviceStatus.Errored) ? this.translate.instant('GENERIC_ERROR') : ((this.deviceStatus.Online) ? this.translate.instant('ONLINE') : this.translate.instant('OFFLINE')) : this.translate.instant('GENERIC_ERROR'));
		}
	}

	getStatusColor(): string {
		if (this.thermo !== undefined && this.thermo !== null) {
			if (this.thermo.error !== null && this.thermo.error !== undefined && this.thermo.error !== "None") {
				return 'warning';
			}
			if (this.thermo.on_line !== null && this.thermo.on_line !== undefined && this.thermo.on_line === 1) {
				return 'success';
			}
			return 'danger';
		} else {
			if (this.deviceStatus === undefined || !this.deviceStatus.Online) {
				return 'danger';
			} else if (this.deviceStatus.Errored) {
				return 'warning';
			} else {
				return 'success';
			}
			//return (this.deviceStatus !== undefined && this.deviceStatus.Errored) ? 'warning' : ((this.deviceStatus !== undefined && this.deviceStatus.Online) ? 'success' : 'danger');
		}
	}

	getTruncate(str: string, lenght: string): string {
		if (this.onExtendedMobile()) {
			return str.slice(0,15) + (str.length > 15 ? '...' : '');
		}
		return lenght === 'long' ? str.slice(0,21) + (str.length > 21 ? '...' : '') : str.slice(0,19) + (str.length > 19 ? '...' : '');
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
